import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { type Observable } from 'rxjs';
import { SettingsFacade } from '@web/store/website/settings/settings.facade';
import { type CatalogProductCard, RecurringMode } from '@web/widgets/product-card/type/product-card-widget.type';
import { type Classes } from 'jss';
import { AspectRatio, type ButtonSettings, type Settings } from '@web/widgets/catalog-products/type';
import { DomSanitizer, type SafeHtml } from '@angular/platform-browser';
import {
    BUTTON_SIZE_CLASS_MAP,
    BUTTON_TYPE_CLASS_MAP,
    DEFAULT_BUTTON_CLASS,
} from '@web/widgets/common/base/base-button/base-button.const';
import { EButtonStyleType } from '@common/enums';
import { type SelectedFileExtended } from '@common/types/file.type';
import { TranslatePipe } from '@ngx-translate/core';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { FmFileUrlPipe } from '@sendpulse/file-manager';

@Component({
    selector: 'web-catalog-product-card',
    templateUrl: './catalog-product-card.component.html',
    styleUrls: ['./catalog-product-card.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [TranslatePipe, TooltipModule, FmFileUrlPipe],
})
export class CatalogProductCardComponent {
    @Input() element: CatalogProductCard;
    @Input() classes: Classes;
    @Input() settings: Settings;
    @Input() basketButton: ButtonSettings;

    private readonly settingsFacade: SettingsFacade = inject(SettingsFacade);
    protected readonly sanitizer: DomSanitizer = inject(DomSanitizer);

    public readonly shopCurrency$: Observable<string> = this.settingsFacade.shopCurrency$;

    public getIcon(icon: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(icon);
    }

    public getImagePaddingTop(image: SelectedFileExtended): number {
        const galleryImageRatio: AspectRatio = this.settings.gallery.aspectRatio;

        switch (galleryImageRatio) {
            case AspectRatio.default:
                return 100;
            case AspectRatio.manual:
                if (!image.resizedHeight || !image.resizedWidth) {
                    return 100;
                }
                return (image.resizedHeight / image.resizedWidth) * 100;
            case AspectRatio.onoToOne:
                return 100;
            case AspectRatio.fourToThree:
                return 75;
            case AspectRatio.threeToFour:
                return 133;
            case AspectRatio.threeToTwo:
                return 66;
            case AspectRatio.twoToThree:
                return 150;
            case AspectRatio.verticalWide:
                return 177;
            case AspectRatio.wide:
                return 56;
        }
    }

    public get buttonClass(): string {
        const styles = `${DEFAULT_BUTTON_CLASS} ${BUTTON_SIZE_CLASS_MAP[this.basketButton.size]}`;

        if (this.basketButton.styleType === EButtonStyleType.custom) {
            return `${styles} ${this.classes?.buttonStyles} ${this.classes?.buttonIconStyles}`;
        }

        return `${styles} ${BUTTON_TYPE_CLASS_MAP[this.basketButton.styleType]} ${this.classes?.buttonIconStyles}`;
    }

    public get subscribePeriodLabel(): string {
        switch (this.element.recurringMode) {
            case RecurringMode.daily:
                return 'widget_product_subscription_period_day';
            case RecurringMode.weekly:
                return 'widget_product_subscription_period_week';
            case RecurringMode.monthly:
                return 'widget_product_subscription_period_month';
            case RecurringMode.quarterly:
                return 'widget_product_subscription_period_3_months';
            case RecurringMode.halfyearly:
                return 'widget_product_subscription_period_6_months';
            case RecurringMode.yearly:
                return 'widget_product_subscription_period_year';
            default:
                return 'widget_product_subscription_period_month';
        }
    }

    public get isSubscriptionType() {
        return Boolean(this.element?.recurringMode);
    }

    protected readonly aspectRatio = AspectRatio;
}
